import React from "react";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {Box, Chip, Colors, Text, Tooltip} from "@bounce/shared/ui";
import {RowActions} from "../../ui/components";
import {TriggerCondition} from "../../apis/requests/triggers/types";
import {conditionChannels, ruleSummary, TemplateChannels} from "./helpers";
import {CodeChip} from "./TriggerBadges";
import {LOCKED_CONDITION_HINT, TIMING_DISPLAY} from "./triggerDisplay";

type Props = {
  id: string;
  priority: number;
  condition: TriggerCondition;
  channels: TemplateChannels;
  onOpen?: () => void;
  onDelete?: () => void;
};

const ConditionCard: React.FC<Props> = ({id, priority, condition, channels, onOpen, onDelete}) => {
  const isDefault = condition.is_default;
  const isLocked = condition.locked && !isDefault;
  const isFixed = isDefault || isLocked;
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id,
    disabled: isFixed,
  });
  const sending = condition.outcome === "send";
  const channelLabels = conditionChannels(condition, channels);

  const card = (
    <Box
      ref={setNodeRef}
      onClick={onOpen}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        p: "12px 14px",
        border: `1px ${isFixed ? "dashed" : "solid"} ${isFixed ? Colors.tertiary._400 : Colors.tertiary._300}`,
        borderRadius: "8px",
        backgroundColor: isFixed ? Colors.tertiary._200 : Colors.tertiary._100,
        cursor: "pointer",
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: "relative",
        zIndex: isDragging ? 1 : 0,
        "&:hover": {borderColor: Colors.blue._400},
      }}
    >
      {isFixed ? (
        <LockOutlinedIcon sx={{fontSize: 18, color: Colors.grey._400, mx: "1px"}} />
      ) : (
        <Box
          {...attributes}
          {...listeners}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{display: "flex", color: Colors.grey._400, cursor: "grab", "&:hover": {color: Colors.grey._700}}}
        >
          <DragIndicatorIcon sx={{fontSize: 20}} />
        </Box>
      )}
      <Box
        sx={{
          width: 24,
          height: 24,
          flexShrink: 0,
          borderRadius: "6px",
          backgroundColor: isFixed ? Colors.tertiary._300 : Colors.blue._100,
          color: isFixed ? Colors.grey._700 : Colors.blue._900,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {priority}
      </Box>
      <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.6}}>
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
          <Text size={14} variant="inter600" sx={{color: Colors.grey._900}}>
            {condition.name}
          </Text>
          {isLocked && <Chip size_="small" variant_="inherit" label="Locked" />}
        </Box>
        {isDefault ? (
          <Text size={12} sx={{color: Colors.grey._600}}>
            Always matches.
          </Text>
        ) : (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
            {condition.rules.map((rule, index) => (
              <CodeChip key={index}>{ruleSummary(rule)}</CodeChip>
            ))}
            <Chip size_="small" variant_="inherit" label={condition.match_mode === "all" ? "Match all" : "Match any"} />
          </Box>
        )}
      </Box>
      <Box sx={{width: 172, flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.25}}>
        <Text size={13} sx={{color: Colors.grey._900}}>
          {sending ? channelLabels.join(" + ") || "No template picked" : "Don't send"}
        </Text>
        {sending && (
          <Text size={12} sx={{color: Colors.grey._600}}>
            {TIMING_DISPLAY[condition.timing].shortLabel}
          </Text>
        )}
      </Box>
      {isLocked ? <Box sx={{width: 36, flexShrink: 0}} /> : <RowActions onEdit={onOpen} onDelete={onDelete} />}
    </Box>
  );

  return isLocked ? (
    <Tooltip title={LOCKED_CONDITION_HINT} placement="top">
      {card}
    </Tooltip>
  ) : (
    card
  );
};

export default ConditionCard;
